import { useCallback, useEffect, useState } from "react";

const ROLE_STAGE_MAP = {
  contador: ["factura"],
  logistica: ["embarque"],
  chofer: ["en_ruta"],
};

const STAGE_LABEL = {
  factura: "Factura",
  embarque: "Embarque",
  en_ruta: "En Ruta",
  entregado: "Entregado",
};

/**
 * Hook to manage browser notifications permission + helper to fire them.
 * Returns { permission, requestPermission, notify, supported }
 */
export function useBrowserNotifications() {
  const supported = typeof window !== "undefined" && "Notification" in window;
  const [permission, setPermission] = useState(supported ? Notification.permission : "denied");

  useEffect(() => {
    if (!supported) return;
    const onChange = () => setPermission(Notification.permission);
    document.addEventListener("visibilitychange", onChange);
    return () => document.removeEventListener("visibilitychange", onChange);
  }, [supported]);

  const requestPermission = useCallback(async () => {
    if (!supported) return "denied";
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result;
    } catch {
      return "denied";
    }
  }, [supported]);

  const notify = useCallback(
    (title, options = {}) => {
      if (!supported || permission !== "granted") return null;
      try {
        const n = new Notification(title, {
          icon: "https://customer-assets.emergentagent.com/job_delivery-status-30/artifacts/fgciba1d_ekin_logo_HR.png",
          badge: "https://customer-assets.emergentagent.com/job_delivery-status-30/artifacts/fgciba1d_ekin_logo_HR.png",
          silent: false,
          ...options,
        });
        n.onclick = () => {
          window.focus();
          n.close();
        };
        return n;
      } catch {
        return null;
      }
    },
    [supported, permission]
  );

  return { supported, permission, requestPermission, notify };
}

/**
 * Returns true if this user's role should be notified for a given delivery stage.
 * Contador → factura (i.e. delivery_created); logística → embarque; chofer → en_ruta.
 */
export function shouldNotifyRole(role, stage) {
  const stages = ROLE_STAGE_MAP[role] || [];
  return stages.includes(stage);
}

export { STAGE_LABEL };
